import React, { useState } from 'react';

import { Button, FormGroup } from 'semantic';
import { useTranslation } from 'react-i18next';
import { size } from 'lodash-es';

import { EditCustomerGroupModal } from '../../customer-groups/CustomerGroup/EditCustomerGroupModal';
import CardStyleRadioButton from 'components/form-fields/CardStyleRadioButton';
import { request } from 'utils/api';
import { CustomerGroup } from 'subdomains/cpo/types/charging-policy';
import { CUSTOMER_GROUPS_V1_SEARCH_API_URL } from 'subdomains/cpo/router/api';
import { AsyncDropdownField } from 'subdomains/cpo/components/AsyncDropdownField';

type Props = {
  values: string[];
  onChange?: (values: string[]) => void;
};

const CustomerGroupSelection: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [isAllSelected, setIsAllSelected] = useState(!props.values?.length);
  const [selectedCustomerGroupIds, setSelectedCustomerGroupIds] = useState<
    string[]
  >(props.values ?? []);

  const handleModeChange = (isAll: boolean) => {
    if (isAll !== isAllSelected) {
      setSelectedCustomerGroupIds([]);
    }
    setIsAllSelected(isAll);
  };

  const handleOnChange = (customerGroupsIds: string[]) => {
    setSelectedCustomerGroupIds(customerGroupsIds);
    props.onChange?.(customerGroupsIds);
  };

  const handleOnCreated = (id: string) => {
    const newSelectedCustomerGroupIds = [...selectedCustomerGroupIds, id!];
    setSelectedCustomerGroupIds(newSelectedCustomerGroupIds);
    props.onChange?.(newSelectedCustomerGroupIds);
  };

  const customerGroupOptionsQueryFn = async (body: object) => {
    const { data } = await request<{ data: CustomerGroup[] }>({
      path: CUSTOMER_GROUPS_V1_SEARCH_API_URL,
      method: 'POST',
      body,
    });

    return data.map((group) => ({
      value: group.id,
      text: group.label,
    }));
  };

  return (
    <>
      <FormGroup style={{ marginLeft: '0', marginRight: '0' }}>
        <CardStyleRadioButton
          isSelected={isAllSelected}
          onClick={() => handleModeChange(true)}
          text={t(
            'providerAdmin.chargingPolicyForm.customerGroupSelection.everyone',
            'Everyone'
          )}
        />
        <CardStyleRadioButton
          isSelected={!isAllSelected}
          onClick={() => handleModeChange(false)}
          text={t(
            'providerAdmin.chargingPolicyForm.customerGroupSelection.specific',
            'Customer Groups'
          )}
        />
      </FormGroup>
      {!isAllSelected && (
        <>
          <AsyncDropdownField
            identifier="customer-group-selection"
            label={t(
              'providerAdmin.chargingPolicyForm.customerGroupSelection.selectCustomerGroupsLabel',
              'Select Customer Groups'
            )}
            multiple
            searchable
            value={selectedCustomerGroupIds}
            onChange={(ids) => handleOnChange(ids as string[])}
            initialOptionsQueryFn={async (value) => {
              const values = value as string[];
              return customerGroupOptionsQueryFn({
                ...(size(values) > 0 ? { ids: values } : {}),
              });
            }}
            searchOptionsQueryFn={async (searchTerm?: string) => {
              return customerGroupOptionsQueryFn({
                ...(searchTerm && { search: searchTerm }),
              });
            }}
          />

          <EditCustomerGroupModal
            trigger={
              <Button
                basic
                icon="plus"
                style={{ marginLeft: '0' }}
                content={t(
                  'providerAdmin.chargingPolicyForm.customerGroupSelection.addCustomerGroupButtonText',
                  'New Customer Group'
                )}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                  e.preventDefault()
                }
              />
            }
            mode="create"
            afterSubmit={(id) => handleOnCreated(id!)}
          />
        </>
      )}
    </>
  );
};

export default CustomerGroupSelection;
